import React, {useState, useMemo} from 'react';
import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Switch,
    Typography,
    Modal,
    Card,
    Divider,
    Button,
    Tooltip
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckIcon from '@mui/icons-material/Check';
import DataTable from "react-data-table-component";
import LoadingComponent from "../../../common/components/LoadingComponent";
import {useGroupList, useModuleList, useUserList, useModulePermissionMutation} from "../../../common/API/hooks";
import Search from "./Search";

const customStyles = {
    rows: {
        style: {
            minHeight: '3.5rem',
        },
    },
    headRow: {
        style: {
            fontWeight: "900",
            backgroundColor: '#F6F6F6',
        },
    },
    pagination: {
        style: {
            backgroundColor: '#ffff',
        },
    },
};

export default function ModulePermissionMatrix({searchQuery, filterUsers, hideEmptyModules}) {
    const [viewMode, setViewMode] = useState('user'); // 'user' or 'group'
    const {data: moduleList, isLoading: moduleLoading} = useModuleList();
    const {data: userList, isLoading: userLoading} = useUserList();
    const {data: groupList, isLoading: groupLoading} = useGroupList();

    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        target: null, // user or group object
        module: null,
        type: 'user' // 'user' or 'group'
    });

    const isLoading = moduleLoading || userLoading || groupLoading;

    const filteredModules = useMemo(() => {
        if (!moduleList) return [];
        let list = moduleList;
        if (searchQuery) {
            list = list.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        if (hideEmptyModules) {
            list = list.filter(m => {
                const hasDirectUsers = m.allowed_users && m.allowed_users.length > 0;
                const hasGroupWithUsers = m.allowed_groups && m.allowed_groups.some(groupName => {
                    const group = groupList?.find(g => g.name === groupName);
                    return group && group.users && group.users.length > 0;
                });
                return hasDirectUsers || hasGroupWithUsers;
            });
        }
        return list;
    }, [moduleList, searchQuery, hideEmptyModules, groupList]);

    const data = useMemo(() => {
        if (viewMode === 'user') {
            if (!userList) return [];
            let list = userList;
            if (filterUsers && filterUsers.length > 0) {
                const filterUsernames = filterUsers.map(u => u.value);
                list = list.filter(u => filterUsernames.includes(u.username));
            }
            return list.map(user => {
                const userModules = filteredModules.filter(m => {
                    const directlyGranted = m.allowed_users?.includes(user.username);
                    const grantedViaGroup = groupList?.some(g => g.users?.includes(user.username) && m.allowed_groups?.includes(g.name));
                    return directlyGranted || grantedViaGroup;
                });
                return {
                    ...user,
                    id: user.id,
                    name: user.username,
                    moduleCount: userModules.length
                };
            });
        } else {
            if (!groupList) return [];
            return groupList.map(group => {
                const groupModules = filteredModules.filter(m => m.allowed_groups.includes(group.name));
                return {
                    ...group,
                    id: group.id,
                    name: group.name,
                    moduleCount: groupModules.length
                };
            });
        }
    }, [viewMode, userList, groupList, filteredModules, filterUsers]);

    const columns = useMemo(() => {
        const baseColumns = [
            {
                name: viewMode === 'user' ? 'User' : 'Group',
                selector: row => row.name,
                sortable: true,
                fixed: 'left',
                width: '200px'
            },
            {
                name: 'Module Count',
                selector: row => row.moduleCount,
                sortable: true,
                width: '120px'
            }
        ];

        const moduleColumns = filteredModules.map(module => ({
            name: module.name,
            sortable: true,
            sortFunction: (rowA, rowB) => {
                const getStatusValue = (row) => {
                    if (viewMode === 'user') {
                        if (module.allowed_users?.includes(row.name)) return 2;
                        if (groupList?.some(g => g.users?.includes(row.name) && module.allowed_groups?.includes(g.name))) return 1;
                        return 0;
                    } else {
                        if (module.allowed_groups?.includes(row.name)) return 2;
                        return 0;
                    }
                };
                return getStatusValue(rowA) - getStatusValue(rowB);
            },
            cell: row => {
                let status = null; // null: no access, 'direct': check, 'group': group icon
                
                if (viewMode === 'user') {
                    const directlyGranted = module.allowed_users?.includes(row.name);
                    const groupsWithAccess = groupList?.filter(g => g.users?.includes(row.name) && module.allowed_groups?.includes(g.name)) || [];
                    
                    if (directlyGranted) {
                        status = 'direct';
                    } else if (groupsWithAccess.length > 0) {
                        status = 'group';
                    }
                } else {
                    const directlyGranted = module.allowed_groups?.includes(row.name);
                    if (directlyGranted) {
                        status = 'direct';
                    }
                }

                const tooltipTitle = status === 'group' 
                    ? `Granted via group(s): ${groupList?.filter(g => g.users?.includes(row.name) && module.allowed_groups?.includes(g.name)).map(g => g.name).join(', ')}`
                    : status === 'direct' 
                        ? 'Directly granted' 
                        : 'No access';

                return (
                    <Box 
                        onClick={() => setModalConfig({
                            isOpen: true,
                            target: row,
                            module: module,
                            type: viewMode
                        })}
                        sx={{ cursor: 'pointer', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '3.5rem' }}
                    >
                        <Tooltip title={tooltipTitle}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                {status === 'direct' && <CheckIcon color="primary" />}
                                {status === 'group' && <GroupsIcon color="action" />}
                            </Box>
                        </Tooltip>
                    </Box>
                );
            },
            width: '150px',
            center: true
        }));

        return [...baseColumns, ...moduleColumns];
    }, [viewMode, filteredModules, groupList]);

    if (isLoading) return <LoadingComponent />;

    return (
        <Box>
            <Grid container alignItems="center" sx={{ mb: 2 }}>
                <Grid item>
                    <Typography variant="body1" sx={{ mr: 2 }}>View by:</Typography>
                </Grid>
                <Grid item>
                    <RadioGroup
                        row
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                    >
                        <FormControlLabel value="user" control={<Radio />} label="User" />
                        <FormControlLabel value="group" control={<Radio />} label="Group" />
                    </RadioGroup>
                </Grid>
            </Grid>
            
            <DataTable
                columns={columns}
                data={data}
                customStyles={customStyles}
                pagination
                persistTableHead
                fixedHeader
                fixedHeaderScrollHeight="600px"
            />

            {modalConfig.isOpen && (
                <PermissionToggleModal 
                    config={modalConfig} 
                    onClose={() => setModalConfig({ ...modalConfig, isOpen: false })} 
                    groupList={groupList}
                />
            )}
        </Box>
    );
}

function PermissionToggleModal({ config, onClose, groupList }) {
    const { target, module, type } = config;
    const { savePermissionAsync } = useModulePermissionMutation({ 
        moduleId: module.id,
        toastMessages: {
            pending: 'Updating permission...',
            success: 'Permission updated successfully',
            error: 'Failed to update permission'
        }
    });

    const [isDirect, setIsDirect] = useState(
        type === 'user' 
            ? module.allowed_users?.includes(target.name)
            : module.allowed_groups?.includes(target.name)
    );

    // For user mode, we might want to see/toggle group permissions too
    const [selectedGroups, setSelectedGroups] = useState(
        type === 'user' 
            ? groupList?.filter(g => g.users?.includes(target.name) && module.allowed_groups?.includes(g.name)).map(g => g.name)
            : []
    );

    const handleSave = async () => {
        let newAllowedUsers = module.allowed_users ? [...module.allowed_users] : [];
        let newAllowedGroups = module.allowed_groups ? [...module.allowed_groups] : [];

        if (type === 'user') {
            if (isDirect) {
                if (!newAllowedUsers.includes(target.name)) newAllowedUsers.push(target.name);
            } else {
                newAllowedUsers = newAllowedUsers.filter(u => u !== target.name);
            }
            // For group permissions, we are toggling groups that the user belongs to
            const userGroups = groupList?.filter(g => g.users?.includes(target.name)).map(g => g.name) || [];
            // Remove all groups the user belongs to from allowed_groups, then add back the selected ones
            newAllowedGroups = newAllowedGroups.filter(g => !userGroups.includes(g));
            newAllowedGroups = [...newAllowedGroups, ...selectedGroups];
        } else {
            if (isDirect) {
                if (!newAllowedGroups.includes(target.name)) newAllowedGroups.push(target.name);
            } else {
                newAllowedGroups = newAllowedGroups.filter(g => g !== target.name);
            }
        }

        await savePermissionAsync({
            allowed_users: newAllowedUsers,
            allowed_groups: newAllowedGroups
        });
        onClose();
    };

    return (
        <Modal open={true} onClose={onClose}>
            <Card sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                p: 3
            }}>
                <Typography variant="h6" gutterBottom>
                    Update Permission
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Module: <b>{module.name}</b><br/>
                    {type === 'user' ? 'User' : 'Group'}: <b>{target.name}</b>
                </Typography>
                
                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                    control={<Switch checked={isDirect} onChange={(e) => setIsDirect(e.target.checked)} />}
                    label={type === 'user' ? "Direct Permission" : "Group Permission"}
                />

                {type === 'user' && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">Via Groups:</Typography>
                        {groupList?.filter(g => g.users?.includes(target.name)).map(group => (
                            <FormControlLabel
                                key={group.name}
                                control={
                                    <Checkbox 
                                        checked={selectedGroups.includes(group.name)} 
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedGroups([...selectedGroups, group.name]);
                                            } else {
                                                setSelectedGroups(selectedGroups.filter(g => g !== group.name));
                                            }
                                        }}
                                    />
                                }
                                label={group.name}
                            />
                        ))}
                    </Box>
                )}

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Confirm</Button>
                </Box>
            </Card>
        </Modal>
    );
}
